import React from "react";
import LegalLayout from "@/components/LegalLayout";
import useSeo from "@/hooks/useSeo";

export default function AGBPage() {
  useSeo({
    title: "Allgemeine Geschäftsbedingungen",
    description:
      "Allgemeine Geschäftsbedingungen (AGB) für Personal Training und Mikronährstoffberatung von Alex Buchner Health.",
    path: "/agb",
  });

  return (
    <LegalLayout title="Allgemeine Geschäftsbedingungen">
      <p className="text-[#1F2937]/70">
        Stand: {new Date().toLocaleDateString("de-DE", { month: "long", year: "numeric" })}
      </p>

      <section data-testid="agb-1">
        <h2 className="font-display text-xl font-bold mt-8 mb-3">
          § 1 Geltungsbereich
        </h2>
        <p>
          Diese Allgemeinen Geschäftsbedingungen (nachfolgend „AGB") gelten für
          sämtliche Verträge über Personal-Training- und
          Mikronährstoffberatungs-Leistungen, die zwischen{" "}
          <strong>Personal Training und Mikronährstoffberatung Alex Buchner</strong>,
          Postfach Traunsteiner Straße 27, 83334 Inzell (nachfolgend „Anbieter")
          und einem Klienten (nachfolgend „Kunde") geschlossen werden.
          Abweichende oder ergänzende Bedingungen werden nur dann
          Vertragsbestandteil, wenn der Anbieter ihrer Geltung ausdrücklich
          schriftlich zugestimmt hat.
        </p>
      </section>

      <section data-testid="agb-2">
        <h2 className="font-display text-xl font-bold mt-8 mb-3">
          § 2 Leistungsumfang
        </h2>
        <p>
          Der Anbieter erbringt folgende Leistungen, je nach individueller
          Vereinbarung:
        </p>
        <ul className="list-disc pl-6 space-y-1.5 mt-2">
          <li>Individuelles Personal Training (vor Ort oder online)</li>
          <li>Mikronährstoffberatung inkl. testbasierter Empfehlungen</li>
          <li>Erstellung individueller Trainings- und Ernährungspläne</li>
          <li>Begleitende Betreuung und Beratung</li>
        </ul>
        <p className="mt-3">
          Konkret vereinbarte Inhalte, Termine und Umfang ergeben sich aus der
          jeweiligen Einzelvereinbarung. Der Anbieter ist kein Arzt und ersetzt
          keine medizinische Diagnose oder Behandlung.
        </p>
      </section>

      <section data-testid="agb-3">
        <h2 className="font-display text-xl font-bold mt-8 mb-3">
          § 3 Vertragsschluss
        </h2>
        <p>
          Die Darstellung der Leistungen auf der Webseite stellt kein rechtlich
          bindendes Angebot dar. Ein Vertrag kommt erst durch ein
          ausdrückliches Angebot des Anbieters (in Textform) und die Annahme
          durch den Kunden zustande. Das kostenlose Erstgespräch begründet
          keinen entgeltlichen Vertrag und stellt lediglich eine unverbindliche
          Kennenlern-Möglichkeit dar.
        </p>
      </section>

      <section data-testid="agb-4">
        <h2 className="font-display text-xl font-bold mt-8 mb-3">
          § 4 Preise und Zahlung
        </h2>
        <p>
          Die jeweils gültigen Preise ergeben sich aus dem individuellen
          Angebot. Alle Preise verstehen sich inklusive der gesetzlichen
          Umsatzsteuer, sofern diese ausgewiesen wird. Sofern nicht abweichend
          vereinbart, sind Rechnungen innerhalb von 14 Tagen nach Erhalt ohne
          Abzug fällig.
        </p>
      </section>

      <section data-testid="agb-5">
        <h2 className="font-display text-xl font-bold mt-8 mb-3">
          § 5 Terminabsagen und Stornierung
        </h2>
        <p>
          Vereinbarte Termine können bis 24 Stunden vor Terminbeginn
          kostenfrei abgesagt oder verschoben werden. Bei späteren Absagen
          oder Nichterscheinen wird die vereinbarte Vergütung in voller Höhe
          fällig, soweit der Anbieter nicht in der Lage war, den Zeitraum
          anderweitig zu nutzen.
        </p>
      </section>

      <section data-testid="agb-6">
        <h2 className="font-display text-xl font-bold mt-8 mb-3">
          § 6 Mitwirkungspflichten und Gesundheit
        </h2>
        <p>
          Der Kunde sichert zu, dass er gesundheitlich in der Lage ist, an
          den vereinbarten Trainings- und Beratungseinheiten teilzunehmen. Bei
          Vorerkrankungen, Schwangerschaft, akuten Beschwerden oder
          Medikamenten-Einnahme ist der Kunde verpflichtet, dies dem Anbieter
          vor Beginn mitzuteilen und gegebenenfalls ärztliche Freigabe
          einzuholen. Der Kunde nimmt auf eigenes Risiko teil.
        </p>
      </section>

      <section data-testid="agb-7">
        <h2 className="font-display text-xl font-bold mt-8 mb-3">
          § 7 Haftung
        </h2>
        <p>
          Der Anbieter haftet unbeschränkt für Vorsatz und grobe
          Fahrlässigkeit sowie nach den Vorschriften des
          Produkthaftungsgesetzes. Für leichte Fahrlässigkeit haftet der
          Anbieter nur bei Verletzung wesentlicher Vertragspflichten (sog.
          Kardinalpflichten), bei einer Verletzung von Leben, Körper oder
          Gesundheit sowie bei Schäden, die in den Schutzbereich einer vom
          Anbieter abgegebenen Garantie fallen. In diesen Fällen ist die
          Haftung auf den vertragstypischen, vorhersehbaren Schaden begrenzt.
        </p>
      </section>

      <section data-testid="agb-8">
        <h2 className="font-display text-xl font-bold mt-8 mb-3">
          § 8 Widerrufsrecht für Verbraucher
        </h2>
        <p>
          Verbrauchern steht bei Fernabsatzverträgen ein gesetzliches
          Widerrufsrecht gemäß § 355 BGB zu. Der Widerruf ist innerhalb von
          14 Tagen ab Vertragsschluss in Textform (z. B. per E-Mail an{" "}
          <a
            href="mailto:info@alex-buchner-health.de"
            className="text-[#2E89C4] hover:underline"
          >
            info@alex-buchner-health.de
          </a>
          ) gegenüber dem Anbieter zu erklären. Beginnt der Anbieter mit
          ausdrücklicher Zustimmung des Kunden vor Ablauf der Widerrufsfrist
          mit der Erbringung der Dienstleistung, erlischt das Widerrufsrecht
          mit vollständiger Erbringung der Dienstleistung.
        </p>
      </section>

      <section data-testid="agb-9">
        <h2 className="font-display text-xl font-bold mt-8 mb-3">
          § 9 Datenschutz
        </h2>
        <p>
          Die Verarbeitung personenbezogener Daten erfolgt gemäß unserer{" "}
          <a href="/datenschutz" className="text-[#2E89C4] hover:underline">
            Datenschutzerklärung
          </a>
          .
        </p>
      </section>

      <section data-testid="agb-10">
        <h2 className="font-display text-xl font-bold mt-8 mb-3">
          § 10 Schlussbestimmungen
        </h2>
        <p>
          Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des
          UN-Kaufrechts. Sollten einzelne Bestimmungen dieser AGB unwirksam
          sein oder werden, bleibt die Wirksamkeit der übrigen Bestimmungen
          unberührt. Anstelle der unwirksamen Bestimmung gilt eine wirksame
          Regelung, die dem wirtschaftlichen Zweck der unwirksamen Bestimmung
          am nächsten kommt.
        </p>
      </section>

      <p className="mt-10 text-xs text-[#1F2937]/55">
        Hinweis: Diese AGB stellen eine Muster-Vorlage dar und sollten vor
        produktivem Einsatz durch eine fachkundige Person geprüft werden.
      </p>
    </LegalLayout>
  );
}
